import React from 'react';
import { trackEvent, AnalyticsConstants } from '../../../../../utilities/analyticsUtilities';
import { Typography, Tabs, Tab, Box, CircularProgress, Tooltip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import SettingsPanelSection from '../SettingsPanelSection';
import { connect } from 'react-redux';
import CircleProgressButton from '../../../../buttons/CircleProgressButton';
import LyricsStyleEditor from '../../components/LyricsEditor/LyricsStyleEditor';
import { createActions } from '../../store';
import CreatePageConstants, { DEFAULT_AUDIO_TRACK } from '../../CreatePageConstants';
import LyricsTextDialog from '../../components/LyricsEditor/LyricsTextDialog';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import ConfirmationDialog from '../../../../dialogs/ConfirmationDialog';
import SettingsPanelAudioButton from '../SettingsPanelAudioButton';
import TooltipHelpButton from '../../../../buttons/TooltipHelpButton';
import { getOSCommandKeyName } from '../../../../../utilities/stringUtilities';
import { saveLyricsPanelVisited } from '../../../../../utilities/storageUtilities';
import TimelineTopBar from '../../components/LyricsEditor/TimelineTopBar';
import SegmentsTimeline from '../../components/LyricsEditor/SegmentsTimeline';
import AppConstants from '../../../../../App/AppConstants';

const userSelectStyles = {
    WebkitTouchCallout: 'none',   /* iOS Safari */
    WebkitUserSelect: 'none',    /* Safari */
    KhtmlUserSelect: 'none',     /* Konqueror HTML */
    MozUserSelect: 'none',       /* Old versions of Firefox */
    MsUserSelect: 'none',        /* Internet Explorer/Edge */
    userSelect: 'none'           /* Non-prefixed version */
};

const styles = theme => ({
    noSelectContainer: {
        '& span': userSelectStyles,
        '& p': userSelectStyles
    },
    tabsContainer: {
        background: theme.palette.action.hover,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
        '& span': userSelectStyles,
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    },
    tab: {
        minWidth: 0,
        transition: theme.transitions.create('background', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
            background: theme.palette.action.hover
        },
        textTransform: 'none',
        fontSize: 15,
        fontWeight: 400,
        '&.Mui-selected': {
            color: "white",
            background: theme.palette.action.selected,
            fontWeight: 500
        }
    },
    emptyText: {
        padding: theme.spacing(2)
    }
});

class SettingsPanelSectionLyrics extends React.Component {

    state = {
        selectedTab: CreatePageConstants.LYRICS_PANEL_TABS.TEXT,
        autoDetectUpgradePromptOpen: false
    };

    render() {
        const { classes, audioUrl, save, audioAnalyzing, savingInProgress, lyricExtractionProcessing, lyricExtractionProgress } = this.props;
        const { selectedTab, autoDetectUpgradePromptOpen } = this.state;

        const settingsPanelVisible = audioUrl && audioUrl != DEFAULT_AUDIO_TRACK.SOUND_URL && !audioAnalyzing;

        return (
            <SettingsPanelSection
                title={this.renderSectionTitle()}
                action={this.renderHeaderActions(settingsPanelVisible, save)}
            >
                {settingsPanelVisible
                    ?
                    lyricExtractionProcessing
                        ?
                        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' style={{ minHeight: 300 }}>
                            <Box position='relative' display='inline-flex'>
                                <CircularProgress
                                    variant={lyricExtractionProgress != null ? 'determinate' : 'indeterminate'}
                                    value={lyricExtractionProgress ?? 0}
                                    size={72}
                                />
                                {lyricExtractionProgress != null &&
                                    <Box
                                        top={0} left={0} bottom={0} right={0}
                                        position='absolute'
                                        display='flex'
                                        alignItems='center'
                                        justifyContent='center'
                                    >
                                        <Typography variant='body1' component='div' style={{ fontWeight: 600 }}>
                                            {`${Math.round(lyricExtractionProgress)}%`}
                                        </Typography>
                                    </Box>
                                }
                            </Box>
                            <Typography variant='body1' color='textSecondary' style={{ marginTop: 16, fontWeight: 500 }}>
                                Extracting lyrics...
                            </Typography>
                            <Typography variant='caption' color='textSecondary' align='center' style={{ marginTop: 12, maxWidth: 315 }}>
                                This may take a few minutes. Auto detection can make mistakes. Be sure to check your lyrics after extraction.
                            </Typography>
                        </Box>
                        :
                        <>
                            <Tabs
                                variant='fullWidth'
                                value={selectedTab}
                                onChange={this.onTabChange}
                                className={classes.tabsContainer}
                            >
                                <Tab
                                    label='Text'
                                    className={classes.tab}
                                    aria-controls='lyrics-text-panel'
                                    classes={{ selected: classes.tabSelected }}
                                />
                                <Tab
                                    label='Styles'
                                    className={classes.tab}
                                    aria-controls='lyrics-styles-panel'
                                    classes={{ selected: classes.tabSelected }}
                                />
                            </Tabs>
                            <Box
                                className={classes.noSelectContainer}
                                style={{ display: selectedTab === CreatePageConstants.LYRICS_PANEL_TABS.TEXT ? null : 'none' }}
                            >
                                {/* Render the top panel with general actions */}
                                <TimelineTopBar />

                                {/* Render the timeline with segments */}
                                <SegmentsTimeline />
                            </Box>
                            <Box
                                className={classes.noSelectContainer}
                                style={{ display: selectedTab === CreatePageConstants.LYRICS_PANEL_TABS.STYLES ? null : 'none' }}
                            >
                                {/* Render the panel with text styles */}
                                <LyricsStyleEditor />
                            </Box>
                            <br />

                            {/* Render the dialog to paste lyrics text */}
                            <LyricsTextDialog />
                        </>
                    :
                    <Typography
                        variant='body2'
                        align='center'
                        className={classes.emptyText}
                    >
                        Upload an MP3 or WAV file to add lyrics.
                    </Typography>
                }

                <ConfirmationDialog
                    open={autoDetectUpgradePromptOpen}
                    onClose={this.onAutoDetectUpgradePromptClose}
                    confirmation={this.onUpgradeConfirm}
                    title='Auto Detect Lyrics'
                    okText={'OPTIONS'}
                    storageKey='AUTO_DETECT_LYRICS_UPGRADE_PROMPT'
                >
                    <Typography component='span'>
                        Add lyrics to your video automatically with one of our paid plans.
                    </Typography>
                </ConfirmationDialog>
            </SettingsPanelSection>
        );
    }

    renderHeaderActions = (settingsPanelVisible, save) => {
        const { classes } = this.props;

        return (
            <div className={classes.actionsRoot}>
                {settingsPanelVisible
                    ? this.renderAutoDetectButton()
                    : <SettingsPanelAudioButton save={save} />
                }
            </div>
        );
    };

    renderAutoDetectButton = () => {
        const { audioUrl, lyricExtractionProcessing, audioAnalyzing, savingInProgress, audioDuration } = this.props;

        const hasUploadedAudio = Boolean(audioUrl) && audioUrl !== DEFAULT_AUDIO_TRACK.SOUND_URL;
        const maxLyricsExtractionDuration = 600; // 10 minutes in seconds
        const exceedsAudioLengthLimit = audioDuration > maxLyricsExtractionDuration;
        const disabled = !hasUploadedAudio || audioAnalyzing || savingInProgress || exceedsAudioLengthLimit;

        const button = (
            <CircleProgressButton
                variant='outlined'
                color='inherit'
                size='small'
                startIcon={<HistoryEduIcon />}
                onClick={this.onAutoDetectClick}
                component='span'
                disabled={disabled || lyricExtractionProcessing}
            >
                AUTO DETECTION
            </CircleProgressButton>
        );

        if (exceedsAudioLengthLimit) {
            return (
                <Tooltip title="Lyrics extraction is only available for tracks under 10 minutes." arrow placement="bottom">
                    <span>{button}</span>
                </Tooltip>
            );
        }

        return button;
    }

    renderSectionTitle = () => {
        const { classes } = this.props;
        const commandKeyName = getOSCommandKeyName();

        return (
            <>
                Lyrics
                <TooltipHelpButton
                    title={
                        <>
                            <Typography variant='subtitle2'>Keyboard Shortcuts:</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Enter</b>: Add lyric segment</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Space</b>: Pause / play preview</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Left Arrow</b>: Seek back</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Right Arrow</b>: Seek forward</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Up Arrow</b>: Move lyrics up</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Down Arrow</b>: Move lyrics down</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Shift + Up Arrow</b>: Shorten lyrics</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Shift + Down Arrow</b>: Expand lyrics</Typography>
                            <br />
                            <Typography variant='caption'><b>Tab</b>: Select next lyrics</Typography>
                            <br />
                            <Typography variant='caption'><b>Shift + Tab</b>: Select previous lyrics</Typography>
                            <br />
                            <Typography variant='caption'><b>{commandKeyName} + Delete</b>: Remove lyric segment</Typography>
                        </>
                    }
                    placement='right'
                />
            </>
        );
    }

    componentDidMount = () => {
        const { setProperty, lyricsPanelVisited } = this.props;
        saveLyricsPanelVisited(true);

        if (!lyricsPanelVisited)
            setProperty('lyricsPanelVisited', true);
    }

    onTabChange = (event, value) => {
        this.setState({ selectedTab: value });
    }

    onAutoDetectClick = () => {
        const { userSubscriptionType, enqueueLyricExtractionJob, videoSettingsId, save, audioDuration } = this.props;
        const isFreeUser = userSubscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE;

        trackEvent('Automated Lyric Detection', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            duration: audioDuration
        });

        if (isFreeUser) {
            this.setState({ autoDetectUpgradePromptOpen: true });
            return;
        }

        save(false, (savedVideoId) => {
            enqueueLyricExtractionJob(savedVideoId);
        });
    }

    onAutoDetectUpgradePromptClose = () => {
        this.setState({ autoDetectUpgradePromptOpen: false });
    }

    onUpgradeConfirm = () => {
        window.location.href = '/plans';
        this.setState({ autoDetectUpgradePromptOpen: false });
    }
}

const mapStateToProps = (state) => {
    const {
        lyricsPanelVisited,
        audioAnalyzing,
        audioDuration,
        lyricExtractionProcessing,
        lyricExtractionProgress
    } = state.create;

    const { url: audioUrl } = state.create.audioSettings;
    const { savingInProgress } = state.create.videoSavingState;
    const { id: videoSettingsId } = state.createPreview.present.videoSettings;
    const { subscriptionType: userSubscriptionType } = state.app.accountDetails;

    return {
        lyricsPanelVisited,
        audioAnalyzing,
        audioDuration,
        audioUrl,
        savingInProgress,
        lyricExtractionProcessing,
        lyricExtractionProgress,
        videoSettingsId,
        userSubscriptionType
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const enqueueLyricExtractionJob = (videoId) => dispatch(createActions.enqueueLyricExtractionJob(videoId));

    return {
        setProperty,
        enqueueLyricExtractionJob
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SettingsPanelSectionLyrics, styles));